import { Link, useNavigate, useSearch } from "@tanstack/react-router"
import { ArrowRight, Search } from "lucide-react"
import { useMemo, useState } from "react"
import { ErrorState } from "@/components/states"
import { Skeleton } from "@/components/ui/skeleton"
import { referencesTo } from "@/lib/defs"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useObjects } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { useOntology } from "@/workspace/hooks"
import { type Ontology, optionLabel, type TypeMeta } from "@/workspace/ontology"
import { RefusalNote } from "@/workspace/values"
import { dataAreaOf, dataAreas } from "./areas"
import { DataFrame, Stat } from "./frame"

const ROLE_KEYS = {
  title: "dv.role.title",
  identifier: "dv.role.identifier",
  status: "dv.role.status",
  money: "dv.role.money",
  currency: "dv.role.currency",
  date: "dv.role.date",
  quantity: "dv.role.quantity",
  reference: "dv.role.reference",
  contact: "dv.role.contact",
  flag: "dv.role.flag",
  text: "dv.role.text",
  technical: "dv.role.technical",
} as const

/** Types grouped by business area, for the explorer's index. */
function useIndex(ontology: Ontology | undefined, query: string) {
  return useMemo(() => {
    if (!ontology) return []
    const q = query.trim().toLowerCase()
    return dataAreas(ontology)
      .map((area) => ({
        area,
        types: area.types
          .map((n) => ontology.types.get(n))
          .filter((t): t is TypeMeta => Boolean(t))
          .filter((t) => !q || t.label.toLowerCase().includes(q) || t.name.toLowerCase().includes(q) || t.fields.some((f) => f.id.includes(q)))
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .filter((g) => g.types.length)
  }, [ontology, query])
}

export function OntologyPage() {
  const t = useT()
  const scope = useScope()
  const navigate = useNavigate()
  const search = useSearch({ from: "/$orgId/$environmentId/data/ontology" })
  const { ontology, defs } = useOntology(scope)
  const [query, setQuery] = useState("")
  const index = useIndex(ontology, query)
  const selectedName = search.type ?? index[0]?.types[0]?.name
  const selected = selectedName ? ontology?.types.get(selectedName) : undefined
  const select = (name: string) => void navigate({ to: "/$orgId/$environmentId/data/ontology", params: scope, search: { type: name } })

  return (
    <DataFrame title={t("dv.nav.ontology")} description={t("dv.ontology.description")} how={["dv.how.ontology"]} crumbs={[t("dv.nav.ontology")]} width="full">
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : (
        <div className="grid gap-8 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <nav aria-label={t("dv.ontology.index")} className="space-y-4 lg:sticky lg:top-0 lg:max-h-[calc(100dvh-10rem)] lg:overflow-y-auto lg:pe-2">
            <label className="relative block">
              <span className="sr-only">{t("dv.ontology.filter")}</span>
              <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground" aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("dv.ontology.filter")}
                className="h-9 w-full rounded-full border border-input bg-background ps-9 pe-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
              />
            </label>
            {index.map(({ area, types }) => (
              <div key={area.id}>
                <p className="px-2 pb-1 text-[11px] font-medium text-muted-foreground">
                  {area.label}
                  {area.core ? <span className="ms-1.5 text-subtle-foreground">{t("ws.areas.core")}</span> : null}
                </p>
                <ul>
                  {types.map((type) => (
                    <li key={type.name}>
                      <button
                        type="button"
                        aria-current={type.name === selectedName ? "true" : undefined}
                        onClick={() => select(type.name)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-start text-sm outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted/60",
                          type.name === selectedName && "bg-accent font-medium",
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate">{type.label}</span>
                        <span className="text-2xs text-subtle-foreground tabular-nums">{type.fields.length}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          {selected ? <TypeDetail key={selected.name} type={selected} ontology={ontology} onSelect={select} /> : null}
        </div>
      )}
    </DataFrame>
  )
}

function TypeDetail({ type, ontology, onSelect }: { type: TypeMeta; ontology: Ontology; onSelect: (name: string) => void }) {
  const t = useT()
  const scope = useScope()
  const formatNumber = useFormatNumber()
  const { defs } = useOntology(scope)
  const area = dataAreaOf(dataAreas(ontology), type.name)
  // Up to 1,000 authorized records: an exact count below that, "1,000+" above it.
  const count = useObjects(scope, type.name, null, 1000)
  const incoming = ontology.incoming.get(type.name) ?? []
  const coreLinks = type.links.filter((l) => ontology.types.get(l.target)?.layer === "registry")
  const fromCore = incoming.filter((l) => ontology.types.get(l.source)?.layer === "registry" || l.extension)
  const artifact = defs.data?.artifacts.find((a) => a.artifactId === type.artifactId)
  const related = useMemo(
    () => (artifact && defs.data ? referencesTo(artifact, defs.data.artifacts).filter((a) => a.kind === "mapping" || a.kind === "source_schema") : []),
    [artifact, defs.data],
  )
  const masked = type.fields.filter((f) => f.markings.length)

  return (
    <article className="min-w-0 space-y-8">
      <header className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {area?.label}
          {type.layer === "registry" ? ` · ${t("ws.areas.core")}` : type.source ? ` · ${t("ws.list.importedFrom", { source: type.source })}` : ""}
        </p>
        <h2 className="text-2xl font-semibold tracking-tight">{type.label}</h2>
        <p dir="ltr" className="font-mono text-xs text-muted-foreground">
          {type.name}
        </p>
        {type.description && !/^Technical view of /.test(type.description) ? <p className="max-w-3xl text-sm text-muted-foreground">{type.description}</p> : null}
        <div className="flex flex-wrap gap-2 pt-1 text-xs">
          <Link to="/$orgId/$environmentId/data/explore" params={scope} search={{ type: type.name }} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 hover:bg-muted/60">
            {t("dv.ontology.explore")} <ArrowRight className="size-3 rtl:-scale-x-100" aria-hidden />
          </Link>
          <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: type.name }} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 hover:bg-muted/60">
            {t("dv.ontology.businessList")}
          </Link>
          <Link to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: "entity", artifactId: type.artifactId }} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 hover:bg-muted/60">
            {t("dv.ontology.definition")}
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-x-8 gap-y-5 border-y border-border py-5 sm:grid-cols-5">
        <Stat
          label={t("dv.ontology.records")}
          value={count.isPending ? <Skeleton className="h-6 w-16" /> : count.isError ? "—" : `${formatNumber(count.data.items.length)}${count.data.nextCursor ? "+" : ""}`}
          hint={count.isError ? t("dv.ontology.countRefused") : t("dv.ontology.recordsHint")}
        />
        <Stat label={t("dv.home.properties")} value={formatNumber(type.fields.length)} />
        <Stat label={t("dv.ontology.linksOut")} value={formatNumber(type.links.length)} />
        <Stat label={t("dv.ontology.linksIn")} value={formatNumber(incoming.length)} />
        <Stat label={t("dv.ontology.marked")} value={formatNumber(masked.length)} />
      </div>
      {count.isError ? <RefusalNote error={count.error} title={t("dv.ontology.countRefused")} /> : null}

      <section className="space-y-3">
        <h3 className="text-[15px] font-semibold tracking-tight">{t("dv.ontology.properties")}</h3>
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-start text-[11px] text-muted-foreground uppercase tracking-[0.06em]">
              <tr className="border-b border-border">
                {[t("dv.ontology.colLabel"), t("dv.ontology.colId"), t("dv.ontology.colType"), t("dv.ontology.colRole"), t("dv.ontology.colMarkings")].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-start font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {type.fields.map((f) => (
                <tr key={f.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2">{f.label}</td>
                  <td dir="ltr" className="px-4 py-2 font-mono text-xs text-muted-foreground">
                    {f.id}
                  </td>
                  <td dir="ltr" className="px-4 py-2 font-mono text-xs">
                    {f.typeName}
                    {f.options.length ? <span className="ms-1.5 font-sans text-subtle-foreground">{f.options.slice(0, 4).map((o) => o.label).join(" · ")}{f.options.length > 4 ? " …" : ""}</span> : null}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{t(ROLE_KEYS[f.role])}</td>
                  <td className="px-4 py-2">
                    <span className="flex flex-wrap gap-1">
                      {f.markings.map((m) => (
                        <span key={m} dir="ltr" className="rounded-full border border-dashed border-border px-2 py-0.5 font-mono text-2xs text-muted-foreground">
                          {m}
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-8 xl:grid-cols-2">
        <div className="space-y-3">
          <h3 className="text-[15px] font-semibold tracking-tight">{t("dv.ontology.links")}</h3>
          <ul className="divide-y divide-border rounded-2xl border border-border text-sm">
            {[...type.links.map((l) => ({ l, out: true })), ...incoming.map((l) => ({ l, out: false }))].map(({ l, out }) => {
              const other = out ? l.target : l.source
              return (
                <li key={`${out}:${l.source}.${l.id}`} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-10 text-2xs text-subtle-foreground">{out ? t("dv.ontology.out") : t("dv.ontology.in")}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {l.label}
                    {l.extension ? <span className="ms-1.5 text-2xs text-subtle-foreground">{t("dv.ontology.declaredBy", { ns: l.extension })}</span> : null}
                  </span>
                  <button type="button" onClick={() => onSelect(other)} className="truncate text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                    {ontology.types.get(other)?.label ?? other}
                  </button>
                  <span className="text-2xs text-subtle-foreground">{out ? (l.toMany ? "1 → n" : "n → 1") : l.toMany ? "n ← 1" : "1 ← n"}</span>
                </li>
              )
            })}
            {!type.links.length && !incoming.length ? <li className="px-4 py-3 text-muted-foreground">{t("dv.ontology.noLinks")}</li> : null}
          </ul>
        </div>
        <div className="space-y-3">
          <h3 className="text-[15px] font-semibold tracking-tight">{t("dv.ontology.core")}</h3>
          <ul className="divide-y divide-border rounded-2xl border border-border text-sm">
            {type.implements.map((i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-2xs text-subtle-foreground">{t("dv.ontology.implements")}</span>
                <span dir="ltr" className="font-mono text-xs">
                  {i}
                </span>
              </li>
            ))}
            {coreLinks.map((l) => (
              <li key={`core:${l.id}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-2xs text-subtle-foreground">{t("dv.ontology.mapsTo")}</span>
                <button type="button" onClick={() => onSelect(l.target)} className="hover:underline">
                  {ontology.types.get(l.target)?.label ?? l.target}
                </button>
                <span className="text-2xs text-subtle-foreground">{t("dv.ontology.via", { link: l.label })}</span>
              </li>
            ))}
            {fromCore.map((l) => (
              <li key={`from:${l.source}.${l.id}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-2xs text-subtle-foreground">{t("dv.ontology.bridgedFrom")}</span>
                <button type="button" onClick={() => onSelect(l.source)} className="hover:underline">
                  {ontology.types.get(l.source)?.label ?? l.source}
                </button>
                <span className="text-2xs text-subtle-foreground">{t("dv.ontology.via", { link: l.label })}</span>
              </li>
            ))}
            {related.map((a) => (
              <li key={a.artifactId} className="flex items-center gap-3 px-4 py-2.5">
                <span className="text-2xs text-subtle-foreground">{a.kind === "mapping" ? t("dv.ontology.mapping") : t("dv.ontology.sourceSchema")}</span>
                <Link to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: a.kind, artifactId: a.artifactId }} dir="ltr" className="truncate font-mono text-xs hover:underline">
                  {a.artifactId}
                </Link>
              </li>
            ))}
            {!type.implements.length && !coreLinks.length && !fromCore.length && !related.length ? <li className="px-4 py-3 text-muted-foreground">{t("dv.ontology.noCore")}</li> : null}
          </ul>
          {type.lifecycle ? (
            <p className="text-xs text-muted-foreground">
              {t("dv.ontology.lifecycle")}: {type.lifecycle.states.map(optionLabel).join(" → ")}
            </p>
          ) : null}
        </div>
      </section>
    </article>
  )
}
